/**
 * 2026-09-28 — live speaker diarization labels for the Voxtral LIVE panel.
 *
 * voxtral-server attributes each caption to anonymous speakers (0-based, in
 * order of first appearance) and the C# relay appends them to the PARTIAL /
 * FINAL log events as
 *   ` ||SPK||{"speaker":0,"turns":[[0,0],[1,7]]}`
 * `turns` lists [speaker, firstWordIndex] and is only filled when a segment
 * has more than one speaker. Word indices (not character offsets), so
 * trimming and UTF-16 counting on this side cannot shift a boundary.
 *
 * Portal display only — FAB never receives speaker labels.
 */

export interface VoxtralSpeaker {
  speaker: number | null;
  turns: Array<[number, number]>;
}

export const VOXTRAL_SPEAKER_SLOTS = 8;

const isIndex = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;

/** Parse the JSON after ` ||SPK||`. Never throws; null for anything unusable. */
export function parseSpeakerMarker(raw: string | null | undefined): VoxtralSpeaker | null {
  if (!raw) return null;
  try {
    return toSpeaker(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** Speaker info of a GET /segments record (`speaker`, `speaker_turns`) — the history seed. */
export function speakerFromRecord(record: any): VoxtralSpeaker | null {
  if (!record || typeof record !== 'object') return null;
  return toSpeaker({ speaker: record.speaker, turns: record.speaker_turns });
}

function toSpeaker(obj: any): VoxtralSpeaker | null {
  if (!obj || typeof obj !== 'object') return null;
  const speaker = isIndex(obj.speaker) ? obj.speaker : null;
  const turns: Array<[number, number]> = Array.isArray(obj.turns)
    ? obj.turns
        .filter((t: unknown) => Array.isArray(t) && t.length === 2 && isIndex(t[0]) && isIndex(t[1]))
        .map((t: number[]) => [t[0], t[1]] as [number, number])
    : [];
  if (speaker === null && turns.length === 0) return null;
  return { speaker, turns };
}

export function speakerLabel(speaker: number): string {
  return `S${speaker + 1}`;
}

export function speakerTitle(speaker: number): string {
  return `Sprecher ${speaker + 1}`;
}

export function speakerClass(speaker: number): string {
  return `voxtral-speaker-${speaker % VOXTRAL_SPEAKER_SLOTS}`;
}

/** Speakers of a segment in order of appearance (the dominant one when there are no turns). */
export function speakerSequence(spk: VoxtralSpeaker | null | undefined): number[] {
  if (!spk) return [];
  if (spk.turns.length > 1) {
    const seq: number[] = [];
    for (const [s] of spk.turns) {
      if (seq[seq.length - 1] !== s) seq.push(s);
    }
    return seq;
  }
  return spk.speaker !== null ? [spk.speaker] : [];
}

/** `text` cut into speaker runs at the turn boundaries; [] for a single-speaker segment. */
export function splitTurns(text: string, spk: VoxtralSpeaker | null | undefined): Array<{ speaker: number; text: string }> {
  if (!spk || spk.turns.length < 2 || !text?.trim()) return [];
  const parts = text.trim().split(/(\s+)/); // words at even indices, whitespace at odd
  const runs: Array<{ speaker: number; text: string }> = [];
  let turn = 0;
  let word = 0;
  let current = { speaker: spk.turns[0][0], text: '' };
  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 0) {
      while (turn + 1 < spk.turns.length && word >= spk.turns[turn + 1][1]) {
        turn++;
        if (current.text.trim()) runs.push({ speaker: current.speaker, text: current.text.trim() });
        current = { speaker: spk.turns[turn][0], text: '' };
      }
      word++;
    }
    current.text += parts[i];
  }
  if (current.text.trim()) runs.push({ speaker: current.speaker, text: current.text.trim() });
  return runs;
}
